// Port of SuiteStory/Continuity.swift (cross-platform spec §10.3): continuity checks. Findings are candidates for a
// second look, phrased as such, and can go to the margin as `Continuity check` agent comments. Each check needs only the
// data it uses: a scene without a story time is simply not checked for time. Messages are the Swift's; dates in them
// are written in `locale` (the Mac writes them in the device's), in the setting's clock.
import type { ProjectRecord, StoryEntity } from '../model/documents.ts';
import { scalarLength, scalarOffsetOfUTF16 } from '../automerge/offsets.ts';
import { sha256Hex } from '../hashing.ts';
import { wordCount } from '../proseMarkdown.ts';
import { isDaylight, moon, phaseName, sunDay } from './astronomy.ts';
import { beatSheetOf, type Beat, type BeatSheet } from '../plan/beatSheet.ts';
import { isFarFromTarget, manuscriptStarts } from '../model/positions.ts';
import { firstMatchIn, icu, matchesIn } from '../text/icuRegex.ts';
import { sentenceRanges } from '../text/sentences.ts';
import { entityNames, mentions, presenceRank } from '../presence.ts';
import { formatInScene, sceneEnd, sceneMoment, sceneZone as zoneOf, type SceneFacts } from './sceneFacts.ts';
import { formatDate, iso8601, weekdayName, wholeYears, zoneNamed } from './zones.ts';
import { distanceText, durationText, placeDistance, shortestJourney, todayTravel, type TravelSettings } from './travel.ts';

export type ContinuityKind = 'two-places' | 'travel' | 'age' | 'weekday' | 'moon' | 'sun' | 'structure';
export const CONTINUITY_KINDS: readonly ContinuityKind[] = ['two-places', 'travel', 'age', 'weekday', 'moon', 'sun', 'structure'];
export const CONTINUITY_LABELS: Record<ContinuityKind, string> = {
  'two-places': 'Two places at once', travel: 'Travel time', age: 'Age', weekday: 'Weekday', moon: 'Moon', sun: 'Daylight', structure: 'Structure',
};

export interface ContinuityFinding {
  id: string;
  kind: ContinuityKind;
  fragmentID: string;
  fragmentTitle: string;
  message: string;
  /** The passage it concerns: Unicode-scalar [start, end) in the fragment's text. */
  range: [number, number];
}

export interface ContinuityOptions {
  /** The beat sheet, for the structure checks. */
  beatSheet?: BeatSheet | null;
  /** The language dates (and distances, by its region) are written in (BCP 47); default en-US. */
  locale?: string;
  /** How people travel in the story (`readTravel`), for the journeys; default today's transport. */
  travel?: TravelSettings | null;
}

/** `ProjectDocument.beatSheet()` (the Plan room's `BeatSheet`, read as `beatSheetOf` reads it), for `beatSheet` above. */
export const readBeatSheet = (project: ProjectRecord): BeatSheet | null => beatSheetOf(project.settings);

/** `Continuity.check`: two places at once, journeys, then each scene's ages, weekdays and sky, then the beat structure. */
export function checkContinuity(scenes: SceneFacts[], entities: readonly StoryEntity[], options: ContinuityOptions = {}): ContinuityFinding[] {
  const locale = options.locale ?? 'en-US';
  return [
    ...twoPlaces(scenes, entities, locale),
    ...journeys(scenes, entities, options.travel ?? todayTravel(), locale),
    ...scenes.flatMap((s) => [...ages(s, entities, locale), ...weekdays(s, locale), ...sky(s, locale)]),
    ...(options.beatSheet ? structure(options.beatSheet, scenes) : []),
  ];
}

// MARK: Helpers

/** `Continuity.finding`: ids come from `key`, the finding's facts (entity ids, the stored story time in ISO 8601, the
 *  words matched), never the message, so two devices adding the same finding to the margin agree on its id. */
export function finding(kind: ContinuityKind, scene: Pick<SceneFacts, 'id' | 'title'>, range: [number, number], key: string, message: string): ContinuityFinding {
  const id = sha256Hex(`${kind}|${scene.id}|${key}`, 12);
  return { id, kind, fragmentID: scene.id, fragmentTitle: scene.title, message, range };
}

function firstMention(entity: StoryEntity, text: string): [number, number] {
  return mentions(text, [entity])[0]?.range ?? [0, Math.min(40, scalarLength(text))];
}

const scalarRange = (text: string, start: number, end: number): [number, number] => [scalarOffsetOfUTF16(text, start), scalarOffsetOfUTF16(text, end)];

// MARK: Structure (the beat sheet)

/** The scene's first line (up to 200 characters), for a margin note to sit on. */
export function openingRange(text: string): [number, number] {
  const line = text.split('\n')[0];
  return [0, Math.min(scalarLength(line), 200)];
}

/** `Continuity.structure`: beats without scenes, scenes without beats, beats whose scenes come out of the sheet's order,
 *  and beats far from where the sheet expects them (by words, in the book as it stands). */
export function structure(sheet: BeatSheet, scenes: SceneFacts[]): ContinuityFinding[] {
  const first = scenes[0];
  if (!sheet.beats.length || !first) return [];
  const known = new Set(sheet.beats.map((b) => b.id));
  const beatOf = (s: SceneFacts) => (s.beatID !== null && known.has(s.beatID) ? s.beatID : null);
  const make = (id: string, s: SceneFacts, message: string): ContinuityFinding =>
    ({ id: 'structure-' + id, kind: 'structure', fragmentID: s.id, fragmentTitle: s.title, message, range: openingRange(s.text) });
  const percent = (f: number) => `${Math.round(f * 100)}%`;
  if (!scenes.some((s) => beatOf(s) !== null)) {
    return [make('none', first, `No scenes are assigned to the ${sheet.name} beats yet. Drag scenes onto beats in Plan ▸ Beats.`)];
  }

  // Where each scene starts, as a fraction of the book's words.
  const words = scenes.map((s) => wordCount(s.text));
  const total = words.reduce((a, b) => a + b, 0);
  const starts = manuscriptStarts(words);
  const firstScene = new Map<string, number>();
  scenes.forEach((s, i) => { const b = beatOf(s); if (b !== null && !firstScene.has(b)) firstScene.set(b, i); });

  const findings: ContinuityFinding[] = [];
  let previous: { beat: Beat; index: number } | null = null;
  sheet.beats.forEach((b, order) => {
    const index = firstScene.get(b.id);
    const target = b.target ?? null;
    if (index === undefined) {
      // Anchor where the beat would be: near its target, else just before the next beat that has scenes.
      let anchor: number;
      if (target !== null) {
        let last = -1;
        starts.forEach((s, i) => { if (s <= target) last = i; });
        anchor = last >= 0 ? last : 0;
      } else {
        const next = sheet.beats.slice(order + 1).map((x) => firstScene.get(x.id)).find((x) => x !== undefined);
        anchor = next !== undefined ? Math.max(0, next - 1) : (previous?.index ?? 0);
      }
      const place = target !== null ? ` The ${sheet.name} sheet puts it around ${percent(target)} of the book, near here.` : '';
      findings.push(make('empty-' + b.id, scenes[anchor], `No scene is assigned to “${b.title}”.${place}`));
      return;
    }
    if (previous && index < previous.index) {
      findings.push(make('order-' + b.id, scenes[index],
        `“${b.title}” starts before “${previous.beat.title}”, which comes earlier on the sheet. Is the order what you want?`));
    }
    if (target !== null && isFarFromTarget(starts[index], target, total)) {
      findings.push(make('place-' + b.id, scenes[index],
        `“${b.title}” starts ${percent(starts[index])} of the way through the book as it stands; the ${sheet.name} sheet puts it near ${percent(target)}.`));
    }
    if (previous === null || index > previous.index) previous = { beat: b, index };
  });
  // A few scenes without a beat are listed one by one; many (a sheet just started) as one finding.
  const loose = scenes.filter((s) => beatOf(s) === null);
  if (loose.length > 10) {
    findings.push(make('unassigned', loose[0],
      `${loose.length} scenes aren't assigned to a beat yet, starting with “${loose[0].title}”. Plan ▸ Beats lists them along the foot of the board.`));
  } else {
    for (const s of loose) findings.push(make('unassigned-' + s.id, s, `“${s.title}” isn't assigned to a beat.`));
  }
  return findings;
}

// MARK: Two places at once

function twoPlaces(scenes: SceneFacts[], entities: readonly StoryEntity[], locale: string): ContinuityFinding[] {
  const findings: ContinuityFinding[] = [];
  for (const character of entities) {
    if (character.kind !== 'character') continue;
    const present = scenes.filter((s) => s.start !== null && s.setting !== null
      && s.presence.some((p) => p.entityID === character.id && presenceRank(p.level) >= presenceRank('on-stage')));
    present.forEach((a, i) => {
      for (const b of present.slice(i + 1)) {
        if (!(a.setting!.id !== b.setting!.id && sceneMoment(a)! < sceneEnd(b)! && sceneMoment(b)! < sceneEnd(a)!)) continue;
        for (const [scene, other] of [[a, b], [b, a]] as const) {
          const when = formatInScene(scene, sceneMoment(scene)!, 'abbreviated', 'shortened', locale);
          findings.push(finding('two-places', scene, firstMention(character, scene.text), `${character.id}|${other.id}|${iso8601(scene.start!)}`,
            `${character.name} is on stage here, at ${scene.setting!.name} on ${when}, and at ${other.setting!.name} in “${other.title}” at an overlapping time.`));
        }
      }
    });
  }
  return findings;
}

// MARK: Journeys

/** `Continuity.travelSlack`: scene lengths are rough, so a journey is only questioned when it needs this many seconds
 *  more than the gap allows. */
export const TRAVEL_SLACK = 5 * 60;

/** `Continuity.journeys`: a character who leaves one setting and is on stage in another sooner than the journey could
 *  take — the least time for the straight-line distance with the usual detour, at the story's fastest mode of travel
 *  (today: by road or air). Only consecutive scenes in story time, with both settings on the map; overlapping scenes are
 *  `twoPlaces`' to report. The finding goes on the arriving scene. */
export function journeys(scenes: SceneFacts[], entities: readonly StoryEntity[], travel: TravelSettings, locale = 'en-US'): ContinuityFinding[] {
  const findings: ContinuityFinding[] = [];
  for (const character of entities) {
    if (character.kind !== 'character') continue;
    const present = scenes
      .filter((s) => sceneMoment(s) !== null && s.setting?.latitude != null && s.setting?.longitude != null
        && s.presence.some((p) => p.entityID === character.id && presenceRank(p.level) >= presenceRank('on-stage')))
      .sort((x, y) => sceneMoment(x)! - sceneMoment(y)!);
    for (let i = 0; i + 1 < present.length; i++) {
      const a = present[i], b = present[i + 1];
      const from = a.setting!, to = b.setting!;
      const left = sceneEnd(a)!, arrived = sceneMoment(b)!;
      if (from.id === to.id || arrived < left) continue;
      const straight = placeDistance(from, to);
      if (straight === null) continue;
      const shortest = shortestJourney(straight, travel);
      if (!((arrived - left) / 1000 + TRAVEL_SLACK < shortest.time)) continue;
      findings.push(finding('travel', b, firstMention(character, b.text), `${character.id}|${a.id}|${iso8601(a.start!)}|${iso8601(b.start!)}`,
        `${character.name} leaves ${from.name} at ${formatInScene(a, left, 'omitted', 'shortened', locale)} (“${a.title}”) and is at ${to.name} by ${formatInScene(b, arrived, 'omitted', 'shortened', locale)}. They’re ${distanceText(straight, locale)} apart: at least ${durationText(shortest.time, locale)} ${shortest.mode}.`));
    }
  }
  return findings;
}

// MARK: Ages

const AGE = icu(String.raw`\b(?:(\d{1,3})|([a-z]+(?:[- ][a-z]+)?))[- ]years?[- ]old\b|\baged (\d{1,3}|[a-z]+(?:-[a-z]+)?)\b`, 'i');

/** `Continuity.number`: digits or English number words ("twenty-three"). */
export function numberFromWords(s: string): number | null {
  if (/^[+-]?\d+$/.test(s)) return Number(s);
  const units = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen',
    'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
  const tens: Record<string, number> = { twenty: 20, thirty: 30, forty: 40, fifty: 50, sixty: 60, seventy: 70, eighty: 80, ninety: 90 };
  const parts = s.toLowerCase().split(/[- ]/).filter((p) => p !== '');
  let total = 0;
  for (const p of parts) {
    const u = units.indexOf(p);
    if (u >= 0) total += u;
    else if (p in tens) total += tens[p];
    else return null;
  }
  return parts.length ? total : null;
}

function ages(scene: SceneFacts, entities: readonly StoryEntity[], locale: string): ContinuityFinding[] {
  if (scene.start === null) return [];
  const start = scene.start;
  const characters = entities.filter((e) => e.kind === 'character' && e.birth !== undefined);
  if (!characters.length) return [];
  const text = scene.text;
  // Sentences with an age in them, found once for every character.
  const aged = sentenceRanges(text).filter(([a, b]) => firstMatchIn(AGE, text, a, b) !== null).map(([a, b]) => ({ a, b, s: text.slice(a, b) }));
  if (!aged.length) return [];
  const findings: ContinuityFinding[] = [];
  for (const character of characters) {
    // Birth dates and story times are both clock readings in the entry zone.
    const actual = wholeYears(character.birth!.getTime(), start, scene.entryTimeZone);
    const names = entityNames(character);
    for (const { a, b, s } of aged) {
      if (!names.some((n) => s.includes(n))) continue;
      for (const m of matchesIn(AGE, text, a, b)) {
        const first = m.slice(1).find((g) => g !== undefined);
        const stated = first !== undefined ? numberFromWords(first) : null;
        if (stated === null || stated === actual) continue;
        const born = formatDate(character.birth!.getTime(), scene.entryTimeZone, 'abbreviated', 'omitted', locale);
        findings.push(finding('age', scene, scalarRange(text, m.index, m.index + m[0].length), `${character.id}|${stated}|${m.index}|${iso8601(start)}`,
          `Check ${character.name}’s age: the text says ${stated}, but born ${born} they’d be ${actual} on ${formatInScene(scene, sceneMoment(scene)!, 'abbreviated', 'omitted', locale)}.`));
      }
    }
  }
  return findings;
}

// MARK: Weekdays

const WEEKDAY = icu(String.raw`\b(Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)s?\b`);
const NOT_THIS_DAY = new Set(['next', 'last', 'every', 'each', 'following', 'previous', 'until', 'till', 'by', 'since']);

/** `Continuity.wordBefore`: the word just before `location` (lowercased), read backwards over at most a few dozen
 *  characters (UTF-16). */
export function wordBefore(location: number, text: string): string {
  let end = location;
  while (end > 0 && text.charCodeAt(end - 1) === 0x20) end -= 1;
  let start = end;
  while (start > 0 && end - start < 32 && text.charCodeAt(start - 1) !== 0x20 && text.charCodeAt(start - 1) !== 0x0a) start -= 1;
  return text.slice(start, end).toLowerCase();
}

function weekdays(scene: SceneFacts, locale: string): ContinuityFinding[] {
  const moment = sceneMoment(scene);
  if (scene.start === null || moment === null) return [];
  const text = scene.text;
  // Only plain mentions: "next Monday", "every Monday", "Mondays" and "Friday's paper" are about other days.
  const found = matchesIn(WEEKDAY, text).filter((m) => {
    const end = m.index + m[0].length;
    const after = text.slice(end, end + 2);
    if (after === "'s" || after === '’s') return false;
    return !m[0].endsWith('s') && !NOT_THIS_DAY.has(wordBefore(m.index, text));
  });
  const first = found[0];
  if (!first) return [];
  const actual = weekdayName(moment, zoneOf(scene));
  if (found.some((m) => m[0] === actual)) return [];
  const stated = first[0];
  return [finding('weekday', scene, scalarRange(text, first.index, first.index + stated.length), `${stated}|${iso8601(scene.start)}`,
    `The text says ${stated}, but ${formatInScene(scene, moment, 'long', 'omitted', locale)} was a ${actual}.`)];
}

// MARK: Sun and moon

const MOON_PHRASES: [RegExp, [number, number], string][] = [
  [icu(String.raw`\bfull[- ]moon\b`, 'i'), [0.85, 1], 'a full moon'],
  [icu(String.raw`\b(new[- ]moon|moonless)\b`, 'i'), [0, 0.15], 'no moon'],
  [icu(String.raw`\bhalf[- ]moon\b`, 'i'), [0.3, 0.7], 'a half moon'],
  [icu(String.raw`\b(crescent moon|sliver of (a |the )?moon|thin moon)\b`, 'i'), [0.02, 0.45], 'a crescent moon'],
];
const SUN = icu(String.raw`\b(sunlight|sunshine|sunlit|sunny|the sun (?:beat|blazed|shone|glared)|(?:afternoon|midday|morning) sun)\b`, 'i');

function sky(scene: SceneFacts, locale: string): ContinuityFinding[] {
  const moment = sceneMoment(scene);
  if (scene.start === null || moment === null) return [];
  const start = scene.start;
  const text = scene.text;
  const findings: ContinuityFinding[] = [];
  const m = moon(moment);
  for (const [regex, [low, high], what] of MOON_PHRASES) {
    const match = firstMatchIn(regex, text);
    if (!match || (m.illumination >= low && m.illumination <= high)) continue;
    findings.push(finding('moon', scene, scalarRange(text, match.index, match.index + match[0].length), `${what}|${iso8601(start)}`,
      `The text has ${what}, but on ${formatInScene(scene, moment, 'abbreviated', 'omitted', locale)} the moon was a ${phaseName(m)} (${Math.round(m.illumination * 100)}% lit).`));
  }
  const place = scene.setting;
  if (place && place.latitude !== undefined && place.longitude !== undefined) {
    const match = firstMatchIn(SUN, text);
    if (match && isDaylight(moment, place.latitude, place.longitude) === false) {
      const day = sunDay(moment, place.latitude, place.longitude, zoneNamed(place.timeZoneID));
      let times = '';
      if (day.sunrise !== null && day.sunset !== null) {
        times = ` (sunrise ${formatInScene(scene, day.sunrise, 'omitted', 'shortened', locale)}, sunset ${formatInScene(scene, day.sunset, 'omitted', 'shortened', locale)})`;
      }
      const word = match[0];
      findings.push(finding('sun', scene, scalarRange(text, match.index, match.index + word.length), `${word.toLowerCase()}|${iso8601(start)}`,
        `The text mentions “${word}”, but at ${formatInScene(scene, moment, 'omitted', 'shortened', locale)} the sun was down in ${place.name}${times}.`));
    }
  }
  return findings;
}
